import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Crosshair, Wifi, Trash2, Download, RefreshCcw, Info } from 'lucide-react';

const LocationDisplay = ({ locations, onDelete, onExport, onClear, onShowInstructions, selectedCoordSystem, onCoordSystemChange }) => {
  const formatCoordinate = (coord, isGeographic = false) => {
    if (isGeographic) {
      return parseFloat(coord).toFixed(7);
    }
    return parseFloat(coord).toFixed(4);
  };

  const coordSystemOptions = [
    { value: 'WGS84', label: 'WGS84 (Lat/Lon)' },
    { value: 'UTM ETRS89 (Uso 27)', label: 'UTM ETRS89 (Uso 27)' },
    { value: 'UTM ETRS89 (Uso 28)', label: 'UTM ETRS89 (Uso 28)' },
    { value: 'UTM ETRS89 (Uso 29)', label: 'UTM ETRS89 (Uso 29)' },
    { value: 'UTM ETRS89 (Uso 30)', label: 'UTM ETRS89 (Uso 30)' },
    { value: 'UTM ETRS89 (Uso 31)', label: 'UTM ETRS89 (Uso 31)' },
    { value: 'UTM ETRS89 (Uso 32)', label: 'UTM ETRS89 (Uso 32)' },
    { value: 'ED50 (Lat/Lon)', label: 'ED50 (Lat/Lon)' },
    { value: 'UTM ED50 (Uso 27)', label: 'UTM ED50 (Uso 27)' },
    { value: 'UTM ED50 (Uso 28)', label: 'UTM ED50 (Uso 28)' },
    { value: 'UTM ED50 (Uso 29)', label: 'UTM ED50 (Uso 29)' },
    { value: 'UTM ED50 (Uso 30)', label: 'UTM ED50 (Uso 30)' },
    { value: 'UTM ED50 (Uso 31)', label: 'UTM ED50 (Uso 31)' },
    { value: 'UTM ED50 (Uso 32)', label: 'UTM ED50 (Uso 32)' },
    { value: 'Web Mercator', label: 'Web Mercator' },
    { value: 'NAD83 (Lat/Lon)', label: 'NAD83 (Lat/Lon)' },
    { value: 'GDA94 (Lat/Lon)', label: 'GDA94 (Lat/Lon)' },
    { value: 'ITRF2008 (Lat/Lon)', label: 'ITRF2008 (Lat/Lon)' },
    { value: 'PZ-90 (Lat/Lon)', label: 'PZ-90 (Lat/Lon)' },
  ];

  return (
    <motion.div
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-b-3xl p-6 shadow-xl mt-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
    >
      <div className="flex flex-wrap gap-4 mb-6 justify-center">
        <motion.button
          onClick={onExport}
          className="flex items-center gap-2 px-5 py-2 bg-green-500 text-white rounded-xl shadow-md hover:bg-green-600 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Download className="w-5 h-5" />
          Exportar CSV
        </motion.button>
        <motion.button
          onClick={onClear}
          className="flex items-center gap-2 px-5 py-2 bg-red-500 text-white rounded-xl shadow-md hover:bg-red-600 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCcw className="w-5 h-5" />
          Limpiar Todo
        </motion.button>
        <motion.button
          onClick={onShowInstructions}
          className="flex items-center gap-2 px-5 py-2 bg-indigo-500 text-white rounded-xl shadow-md hover:bg-indigo-600 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Info className="w-5 h-5" />
          Instrucciones
        </motion.button>
      </div>

      <div className="mb-6 text-center">
        <label htmlFor="coordSystemSelect" className="block text-gray-700 text-lg font-semibold mb-2">
          Elige tu sistema de coordenadas favorito:
        </label>
        <select
          id="coordSystemSelect"
          value={selectedCoordSystem}
          onChange={(e) => onCoordSystemChange(e.target.value)}
          className="block w-full max-w-xs mx-auto p-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
        >
          {coordSystemOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {locations.length === 0 ? (
        <motion.div
          className="text-center text-gray-500 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">¡Aún no hay ubicaciones! Pulsa "Obtener Ubicación" para empezar la aventura.</p>
        </motion.div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-xl shadow-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">ID</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Coordenadas (X, Y, Z)</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Origen</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Precisión (m)</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Sistema Coordenadas</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {locations.map((loc) => (
                  <motion.tr
                    key={loc.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 text-sm text-gray-800">{loc.id}</td>
                    <td className="py-3 px-4 text-sm text-gray-800">
                      X: {formatCoordinate(loc.x, loc.coordSystem.includes('Lat/Lon'))}, Y: {formatCoordinate(loc.y, loc.coordSystem.includes('Lat/Lon'))}, Z: {formatCoordinate(loc.z)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 flex items-center gap-2">
                      {loc.source === 'Crosshair' ? <Crosshair className="w-4 h-4 text-blue-500" /> : <Wifi className="w-4 h-4 text-purple-500" />}
                      {loc.source}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800">{formatCoordinate(loc.accuracy)}</td>
                    <td className="py-3 px-4 text-sm text-gray-800">{loc.coordSystem}</td>
                    <td className="py-3 px-4 text-sm text-gray-800">
                      <motion.button
                        onClick={() => onDelete(loc.id)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Trash2 className="w-5 h-5" />
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};

export default LocationDisplay;